import React, { useState, useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import styles from './MediaLibraryModal.module.scss';
import adminService from '../../../services/adminService';
import { 
  IoClose, IoFolderOpen, IoArrowUp, IoSearchOutline, 
  IoAdd, IoCloudUploadOutline, IoTrash, IoPencil, 
  IoArrowForwardCircle, IoHome 
} from 'react-icons/io5';
import FileDetailModal from '../FileDetailModal/FileDetailModal';

const BACKEND_URL = 'https://huongtramcomoc.vn';

const MediaLibraryModal = ({ isOpen, onClose, onFileSelect, isInline = false }) => {
  // --- STATE DỮ LIỆU ---
  const [currentFolderId, setCurrentFolderId] = useState(0); // 0 là root
  const [parentId, setParentId] = useState(null);
  const [folderName, setFolderName] = useState('Root'); // Tên folder hiện tại
  
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [pagination, setPagination] = useState({});
  
  const [selectedFile, setSelectedFile] = useState(null); 
  const [editingFile, setEditingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // --- STATE MODAL CON ---
  const [isFileDetailModalOpen, setIsFileDetailModalOpen] = useState(false);
  
  // --- STATE DI CHUYỂN (MOVE) ---
  const [moveItem, setMoveItem] = useState(null); // Item đang muốn chuyển đi
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [allFoldersTree, setAllFoldersTree] = useState([]); // Danh sách folder để chọn đích đến

  const fileInputRef = useRef(null);

  // 1. TẢI DỮ LIỆU
  const loadMedia = (folderId, page = 1) => {
    setIsLoading(true);
    adminService.getMedia(folderId, page)
      .then(res => {
        setParentId(res.data.parent_id);
        setFolders(res.data.folders);
        setFiles(res.data.files);
        setPagination(res.data.pagination);
        
        // Cập nhật tên folder hiện tại (Nếu API trả về, nếu không thì logic tạm)
        // Lưu ý: API getMedia nên trả về thông tin folder hiện tại để làm breadcrumb chính xác
        if(res.data.current_folder_name) setFolderName(res.data.current_folder_name);
        else if (folderId === 0) setFolderName("Thư mục gốc");

        setSelectedFile(null);
      })
      .catch(err => console.error("Lỗi tải media:", err))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (isOpen || isInline) {
      loadMedia(currentFolderId);
    }
  }, [currentFolderId, isOpen, isInline]);

  // 2. CÁC HÀM XỬ LÝ CƠ BẢN
  const handleFileChange = (e) => {
    const filesToUpload = e.target.files;
    if (filesToUpload.length > 0) {
      setUploading(true);
      const formData = new FormData();
      formData.append('folder_id', currentFolderId);
      for (let i = 0; i < filesToUpload.length; i++) {
        formData.append('files[]', filesToUpload[i]);
      }
      adminService.uploadMedia(currentFolderId, formData)
        .then(() => {
          loadMedia(currentFolderId);
        })
        .catch(err => alert("Lỗi tải lên: " + err))
        .finally(() => setUploading(false));
    }
  };
  
  const handleCreateFolder = () => {
    const name = prompt("Nhập tên thư mục mới:");
    if (name) {
      adminService.createMediaFolder(name, currentFolderId)
        .then(() => loadMedia(currentFolderId))
        .catch(err => alert("Lỗi: " + err));
    }
  };

  // --- TÍNH NĂNG MỚI: ĐỔI TÊN THƯ MỤC ---
  const handleRenameFolder = (e, folder) => {
    e.stopPropagation();
    const newName = prompt("Đổi tên thư mục:", folder.name);
    if (newName && newName !== folder.name) {
      adminService.updateMediaFolder(folder.id, newName)
        .then(() => loadMedia(currentFolderId))
        .catch(err => alert("Lỗi: " + err.response?.data?.message || err.message));
    }
  };

  // --- TÍNH NĂNG MỚI: DI CHUYỂN (MOVE) ---
  const handleStartMove = (e, item, type) => {
    e.stopPropagation();
    // Load danh sách tất cả folder để chọn đích đến (chỉ lấy tên và id)
    // Ở đây ta gọi lại getMedia root hoặc API riêng getFolderTree. 
    // Tạm dùng getMedia(0) và lấy folders cấp 1, hoặc làm đơn giản: chuyển về Parent hoặc Root.
    // Để chuyên nghiệp, ta nên có API lấy toàn bộ cấu trúc cây thư mục.
    // Ở ví dụ này: Tôi sẽ hiển thị danh sách folder hiện có + nút về Root.
    
    setMoveItem({ ...item, type }); // type: 'file' | 'folder'
    setAllFoldersTree(folders); // Tạm thời chỉ cho move vào các folder anh em hoặc về root
    setIsMoveModalOpen(true);
  };

  const handleConfirmMove = (targetFolderId) => {
    if (!moveItem) return;
    
    adminService.moveMediaItem(moveItem.id, moveItem.type, targetFolderId)
      .then(() => {
        setIsMoveModalOpen(false);
        setMoveItem(null);
        loadMedia(currentFolderId); // Reload lại trang hiện tại
      })
      .catch(err => alert("Lỗi di chuyển: " + err.response?.data?.message));
  };

  // Xóa
  const handleDeleteFolder = (e, folderId, name) => {
    e.stopPropagation();
    if (window.confirm(`Xóa thư mục "${name}" và toàn bộ nội dung bên trong?`)) {
      adminService.deleteMediaFolder(folderId)
        .then(() => loadMedia(currentFolderId))
        .catch(err => alert("Lỗi: " + err.response?.data?.message));
    }
  };

  const handleDeleteFile = (e, fileId, name) => {
    e.stopPropagation();
    if (window.confirm(`Xóa file "${name}" vĩnh viễn?`)) {
      adminService.deleteMediaFile(fileId)
        .then(() => loadMedia(currentFolderId))
        .catch(err => alert("Lỗi: " + err.response?.data?.message));
    }
  };

  // UI Components
  const MoveModal = () => {
    if (!isMoveModalOpen) return null;
    return (
      <div className={styles.moveModalOverlay}>
        <div className={styles.moveModal}>
          <h4>Di chuyển "{moveItem?.name || moveItem?.file_name}" đến:</h4>
          <div className={styles.folderList}>
            {/* Luôn có tùy chọn về Root nếu đang không ở Root */}
            {currentFolderId !== 0 && (
              <button onClick={() => handleConfirmMove(0)} className={styles.folderOption}>
                <IoHome /> Thư mục gốc
              </button>
            )}
            {/* Tùy chọn về thư mục cha nếu có */}
            {parentId !== null && parentId !== 0 && (
              <button onClick={() => handleConfirmMove(parentId)} className={styles.folderOption}>
                <IoArrowUp /> Lên cấp cha
              </button>
            )}
            
            <div className={styles.divider}>Thư mục con hiện tại:</div>
            
            {folders.map(f => (
               // Không cho phép move folder vào chính nó
               (moveItem.type === 'folder' && moveItem.id === f.id) ? null : (
                <button key={f.id} onClick={() => handleConfirmMove(f.id)} className={styles.folderOption}>
                  <IoFolderOpen /> {f.name}
                </button>
               )
            ))}
            {folders.length === 0 && <p className={styles.emptyText}>Không có thư mục con nào.</p>}
          </div>
          <button onClick={() => setIsMoveModalOpen(false)} className={styles.cancelBtn}>Hủy bỏ</button>
        </div>
      </div>
    );
  };

  const MainContent = () => (
    <div className={styles.layout}>
      {/* 1. HEADER & TOOLBAR */}
      <div className={styles.topBar}>
        <div className={styles.breadcrumb}>
          <button onClick={() => setCurrentFolderId(0)} disabled={currentFolderId === 0}>
             <IoHome />
          </button>
          <span>/</span>
          <span className={styles.currentName}>{folderName}</span>
        </div>

        <div className={styles.actions}>
           <button 
             className={styles.actionBtn} 
             onClick={() => setCurrentFolderId(parentId)} 
             disabled={!parentId && parentId !== 0}
             title="Lên 1 cấp"
           >
             <IoArrowUp />
           </button>
           <button className={styles.actionBtn} onClick={handleCreateFolder} title="Tạo thư mục">
             <IoAdd /> Folder
           </button>
           <button 
              className={`${styles.actionBtn} ${styles.uploadBtn}`} 
              onClick={() => fileInputRef.current.click()} 
              disabled={uploading}
           >
             <IoCloudUploadOutline /> {uploading ? 'Uploading...' : 'Upload'}
           </button>
           <input type="file" ref={fileInputRef} onChange={handleFileChange} multiple hidden accept="image/*" />
        </div>
      </div>

      {/* 2. GRID VIEW */}
      <div className={styles.gridContainer}>
        {isLoading ? <div className={styles.loading}>Đang tải dữ liệu...</div> : (
          <>
             {/* FOLDERS */}
             {folders.map(folder => (
              <div 
                key={`f-${folder.id}`} 
                className={styles.cardItem} 
                onClick={() => setCurrentFolderId(folder.id)}
              >
                <div className={styles.iconArea}>
                  <IoFolderOpen className={styles.folderIcon} />
                </div>
                <div className={styles.infoArea}>
                  <span className={styles.name} title={folder.name}>{folder.name}</span>
                </div>
                
                {/* ACTION OVERLAY */}
                <div className={styles.overlayActions}>
                  <button title="Đổi tên" onClick={(e) => handleRenameFolder(e, folder)}><IoPencil /></button>
                  <button title="Di chuyển" onClick={(e) => handleStartMove(e, folder, 'folder')}><IoArrowForwardCircle /></button>
                  <button title="Xóa" className={styles.del} onClick={(e) => handleDeleteFolder(e, folder.id, folder.name)}><IoTrash /></button>
                </div>
              </div>
            ))}

            {/* FILES */}
            {files.map(file => (
              <div 
                key={`i-${file.id}`} 
                className={`${styles.cardItem} ${selectedFile?.id === file.id ? styles.active : ''}`}
                onClick={() => setSelectedFile(file)}
                onDoubleClick={() => { setEditingFile(file); setIsFileDetailModalOpen(true); }}
              >
                <div className={styles.imgArea}>
                  <img src={`${BACKEND_URL}${file.file_path}`} alt={file.file_name} loading="lazy" />
                </div>
                <div className={styles.infoArea}>
                  <span className={styles.name} title={file.file_name}>{file.file_name}</span>
                </div>

                {/* ACTION OVERLAY */}
                <div className={styles.overlayActions}>
                   <button title="Chi tiết / Sửa" onClick={(e) => { e.stopPropagation(); setEditingFile(file); setIsFileDetailModalOpen(true); }}>
                      <IoPencil />
                   </button>
                   <button title="Di chuyển" onClick={(e) => handleStartMove(e, file, 'file')}>
                      <IoArrowForwardCircle />
                   </button>
                   <button title="Xóa" className={styles.del} onClick={(e) => handleDeleteFile(e, file.id, file.file_name)}>
                      <IoTrash />
                   </button>
                </div>
              </div>
            ))}

            {folders.length === 0 && files.length === 0 && (
              <div className={styles.emptyState}>Thư mục trống</div>
            )}
          </>
        )}
      </div>

      {/* 3. PAGINATION */}
      {pagination.total_pages > 1 && (
        <div className={styles.pagination}>
          <button 
            disabled={pagination.page <= 1} 
            onClick={() => loadMedia(currentFolderId, pagination.page - 1)}
          >Trước</button>
          <span>Trang {pagination.page} / {pagination.total_pages}</span>
          <button 
            disabled={pagination.page >= pagination.total_pages} 
            onClick={() => loadMedia(currentFolderId, pagination.page + 1)}
          >Sau</button>
        </div>
      )}

      {/* MOVE MODAL INTERNAL */}
      <MoveModal />
    </div>
  );

  // --- RENDER CHÍNH ---
  if (isInline) {
    return (
      <div className={styles.inlineWrapper}>
        <div className={styles.inlineHeader}>
          <h3>Thư viện Media</h3>
        </div>
        <MainContent />
        {/* Modal chi tiết file */}
        <FileDetailModal 
          isOpen={isFileDetailModalOpen}
          onClose={() => setIsFileDetailModalOpen(false)}
          file={editingFile}
          onSaveSuccess={() => loadMedia(currentFolderId, pagination.page)}
        />
      </div>
    );
  }

  return (
    <>
      <ReactModal 
        isOpen={isOpen} 
        onRequestClose={onClose} 
        className={styles.modalContent} 
        overlayClassName={styles.modalOverlay}
        ariaHideApp={false}
      >
        <div className={styles.modalHeader}>
          <h3>Chọn tập tin</h3>
          <button onClick={onClose}><IoClose /></button>
        </div>
        
        <div className={styles.modalBody}>
          <MainContent />
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.selectedInfo}>
            {selectedFile ? `Đã chọn: ${selectedFile.file_name}` : 'Chưa chọn file nào'}
          </div>
          <div className={styles.footerBtns}>
            <button onClick={onClose} className={styles.btnCancel}>Hủy</button>
            <button 
              onClick={() => { if (selectedFile) { onFileSelect(selectedFile); onClose(); } }} 
              className={styles.btnConfirm} 
              disabled={!selectedFile}
            >
              Chọn File
            </button>
          </div>
        </div>
      </ReactModal>
      
      {/* Modal chi tiết file (Nằm ngoài ReactModal chính để tránh lỗi lồng nhau) */}
      <FileDetailModal 
        isOpen={isFileDetailModalOpen}
        onClose={() => setIsFileDetailModalOpen(false)}
        file={editingFile}
        onSaveSuccess={() => loadMedia(currentFolderId, pagination.page)}
      />
    </>
  );
};

export default MediaLibraryModal;